import {
  StepAccordion,
  AccordionMinimumHeight,
  ButtonColorEnum,
  AccordionBackgroundColor,
} from "../../../../Utils/StepAccordion";
import { InputComponent } from "../../../../Utils/UI/InputComponent/InputComponent";
import IStepInterface from "../../../../../Interfaces/IStepInterface";
import "./NameCprStep.scss";
import Column from "../../../../Utils/Column/Column";
import { useEffect, useRef, useState, useContext } from "react";
import { ValidateUserProfile } from "../../../../../Services/ValidationService/ValidationService";
import PartnerContentContext from "../../../../../Contexts/PartnerContentContext";
import CallMeUpComponent from "../../../../SharedComponents/CallMeUpComponent/CallMeUpComponent";
import { StepEnum } from "../../../../../Models/StepEnum";
import { InfoTooltip } from "../../../../Utils/UI/InfoBadge/InfoBadge";
import { Box, Dialog, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useOrder } from "../../../../../Contexts/OrderContext";

const NameCprStep = ({
  nextStep,
  previousStep,
  open,
  toggleOpen,
  datahubAddress,
  setErrorMode,
}: IStepInterface) => {
  const { order, getOrder, updateOrder } = useOrder();
  const [allowNext, setAllowNext] = useState(true);
  const [idqTries, setIdqTries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [cprError, setCprError] = useState("");
  const partnerContentContext = useContext(PartnerContentContext);
  const DOMPurify = require("dompurify")(window);
  const [existingCustomerModalOpen, setExistingCustomerModalOpen] =
    useState(false);
  const validationRequestId = useRef(0);

  useEffect(() => {
    validationRequestId.current += 1;
    setIsLoading(false);
  }, [order.deliveryAddressId]);

  useEffect(() => {
    setExistingCustomerModalOpen(order.existingCustomer);
  }, [order.existingCustomer]);

  const handleCprChange = (val: { value: string; valid: boolean; error: string }): any => {
    updateOrder({
      cpr: val.valid ? val.value : "",
      IDQValid: false,
      DatahubValid: false,
      existingCustomer: false,
    });
    setAllowNext(true);
  };

  const handleNameChange = (val: { value: string; valid: boolean; error: string }): any => {
    updateOrder({
      fullName: val.valid ? val.value : "",
      IDQValid: false,
      DatahubValid: false,
      existingCustomer: false,
    });
    setAllowNext(true);
  };

  const formValid = () => {
    return !!order.cpr && !!order.fullName && allowNext;
  };

  const resolveValidation = (
    iDQResult: any,
    datahubResult: any,
    sonwinResult: any
  ) => {
    const IDQValid =
      iDQResult.value.data === "Valid" || iDQResult.value.status === 500;
    const DatahubValid =
      datahubResult.value.data?.validationResult === "Valid" ||
      datahubResult.value.status === 500;
    const InDelivery =
      sonwinResult.value.data?.inDelivery && sonwinResult.value.status !== 500;
    const InBinding =
      sonwinResult.value.data?.bindingDate !== "" &&
      sonwinResult.value.status !== 500;
    updateOrder({ IDQValid, DatahubValid, InDelivery, InBinding });
    setIsLoading(false);

    if (datahubResult.value.data?.validationResult === "NoMeteringPointsFound") {
      setAllowNext(false);
      setErrorMode("invalid-meteringpoint");
      return;
    }

    if (iDQResult.value.status === 403) {
      setAllowNext(false);
      setCprError("IDQError");
    } else if (
      IDQValid &&
      DatahubValid &&
      !InBinding &&
      (!InDelivery ||
        (partnerContentContext.resaleEnabled && InDelivery))
    ) {
      setCprError("");
      nextStep();
    } else if (!IDQValid) {
      setIdqTries(idqTries + 1);
      setAllowNext(false);
      if (idqTries >= 2) {
        setCprError("IDQError");
      }
    } else if (!DatahubValid) {
      nextStep();
    } else if (
      InBinding ||
      (!partnerContentContext.resaleEnabled && InDelivery)
    ) {
      if (partnerContentContext.chargingStandProductIds.length === 0) {
        setErrorMode("ExistingCustomer");
      } else {
        updateOrder({ existingCustomer: true });
      }
    } else {
      setErrorMode("FAQ");
    }
  };

  const ValidateAndNext = async () => {
    if (order.existingCustomer) nextStep();
    setIsLoading(true);
    const requestId = ++validationRequestId.current;
    const deliveryAddressId = order.deliveryAddressId;
    const resolveCurrentAddressValidation = (
      iDQResult: any,
      datahubResult: any,
      sonwinResult: any
    ) => {
      if (
        requestId !== validationRequestId.current ||
        getOrder().deliveryAddressId !== deliveryAddressId
      ) {
        return;
      }
      resolveValidation(iDQResult, datahubResult, sonwinResult);
    };

    ValidateUserProfile(
      order.cpr,
      order.fullName,
      datahubAddress,
      order.deliveryAddressId,
      resolveCurrentAddressValidation,
      false,
      partnerContentContext.partnerId
    );
  };

  const retry = () => {
    setIdqTries(0);
    setCprError("");
  };

  return (
    <div>
      <StepAccordion
        loading={isLoading}
        loadingText={partnerContentContext.stepLoader.text}
        loadingTitle={partnerContentContext.stepLoader.title}
        label={partnerContentContext.cprStep.title}
        open={open}
        click={toggleOpen}
        firstButtonLabel={
          partnerContentContext.cprStep.backButton
            ? partnerContentContext.cprStep.backButton
            : "Tilbage"
        }
        firstButtonOutline={true}
        firstButton={previousStep}
        secondButtonLabel={
          partnerContentContext.cprStep.nextButton
            ? partnerContentContext.cprStep.nextButton
            : "Næste"
        }
        secondButtonDisabled={!formValid()}
        secondButtonType={
          formValid() ? ButtonColorEnum.DARK_GREEN : ButtonColorEnum.GREY
        }
        secondButton={ValidateAndNext}
        minimumHeight={AccordionMinimumHeight.SMALL}
        firstButtonId="NameCprStepReturn"
        secondButtonId="NameCprStepForward"
        secondButtonShadow={true}
        backgroundColor={AccordionBackgroundColor.GREY}
      >
        <Column fullWidth={true}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <InputComponent
              placeholder={
                partnerContentContext.cprStep.namePlaceholder
                  ? partnerContentContext.cprStep.namePlaceholder
                  : "Navn"
              }
              validationType="name"
              change={handleNameChange}
            />

            <Box sx={{ padding: 0, margin: "0 0 0 1rem" }}>
              <InfoTooltip
                placement="right"
                message="Skriv dit navn, som det står på dit sundhedskort (sygesikringskort)."
              />
            </Box>
          </Box>
        </Column>

        <Column fullWidthMobile={true}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <InputComponent
              placeholder={
                partnerContentContext.cprStep.cprPlaceholder
                  ? partnerContentContext.cprStep.cprPlaceholder
                  : "CPR-nummer"
              }
              validationType="cpr_age"
              change={handleCprChange}
            />

            <Box sx={{ padding: 0, margin: "0 0 0 1rem" }}>
              <InfoTooltip
                placement="right"
                message="Norlys Energi bruger dit CPR-nummer til kreditvurdering og oprettelse af dig som kunde. Du kan finde mere information i de gældende betingelser"
              />
            </Box>
          </Box>
        </Column>

        {!allowNext && !order.IDQValid && (
          <div className="error-section">
            {partnerContentContext.cprStep.idqUnvalid}
          </div>
        )}
      </StepAccordion>

      <Dialog open={cprError !== ""} onClose={() => { retry(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 1 }}>
          <IconButton onClick={() => { retry(); }} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <div className="outer-container">
          <h1 className="headerText">{partnerContentContext.idqError.title}</h1>

          <div
            className="text"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(partnerContentContext.idqError.text),
            }}
          >
          </div>

          <CallMeUpComponent
            order={order}
            address={datahubAddress}
            showSecondButton={false}
            idqError={true}
            step={StepEnum.CprStep}
          />
        </div>
      </Dialog>

      <Dialog open={existingCustomerModalOpen} onClose={() => setExistingCustomerModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 1 }}>
          <IconButton onClick={() => setExistingCustomerModalOpen(false)} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <div className="outer-container">
          <h1 className="headerText">Eksisterende kunde</h1>

          <p style={{ fontSize: "18px" }}>
            Det ser ud som om du allerede har en el-aftale hos Norlys, vi kan
            derfor ikke tilbyde rabat på køb af el-aftale. Du vil stadig modtage
            rabat på køb af andre produkter.
          </p>
        </div>
      </Dialog>
    </div>
  );
};

export default NameCprStep;
